/**
 * micro:bit 놀이터 3D 무대 묶음 만들기(2026-10-08) — 학생 3D 무대(MbStage)와 운영 콘솔 무대 꾸밈 미리보기가 같은 것을 쓴다.
 * 템플릿(lib/stageTemplate)마다 무대 몸체(받침대 있음 · 없음) · 첫 자세 · 무대 위 묶음(주인공 · 달리기 길 · 격자 · 광장)을 만든다.
 */
import type { MbRunTemplate } from "@picnic-field/renderer";
import type { MbTemplate } from "./stageTemplate";
import type { MbPose } from "./stageActions";

export const MB_BODIES = [
  {
    index: 0,
    kind: "chassis",
    owner: "me",
    shape: { type: "box" as const, half: [0.06, 0.1, 0.06] as const },
  },
  {
    index: 1,
    kind: "env",
    owner: "env",
    surface: "ground",
    shape: { type: "box" as const, half: [0.8, 0.1, 0.8] as const },
  },
  {
    index: 2,
    kind: "env",
    owner: "env",
    surface: "pedestal",
    shape: { type: "box" as const, half: [0.25, 0.03, 0.25] as const },
  },
];
// 주인공은 받침대 윗면(y 0.06)에 · 무대 판 윗면 0 · 받침대 가운데 0.03
export const MB_TF = new Float32Array([
  0, 0.06, 0, 0, 0, 0, 1, 0, -0.1, 0, 0, 0, 0, 1, 0, 0.03, 0, 0, 0, 0, 1,
]);
// 달리기 길 · 격자 — 받침대 없이 무대 판 위(원점)
export const MB_FLAT_BODIES = MB_BODIES.slice(0, 2);
export const MB_FLAT_TF = new Float32Array([0, 0, 0, 0, 0, 0, 1, 0, -0.1, 0, 0, 0, 0, 1]);

/** 무대 위 묶음 — 받침대 주인공 또는 템플릿(달리기 길 · 격자) */
export interface StageObj {
  readonly object: MbRunTemplate["object"];
  update(t: MbTemplate): void;
  applyPose(p: MbPose): void;
  /** 계속 그려야 하면 true */
  animate(tS: number): boolean;
  /** 말풍선 높이(m) */
  readonly lift: number;
  dispose(): void;
}

export async function buildMbStage(t: MbTemplate): Promise<StageObj> {
  const r = await import("@picnic-field/renderer");
  if (t.kind === "run") {
    const run = r.createRunTemplate({ marks: t.marks, obstacles: t.obstacles });
    return {
      object: run.object,
      update: (x) => x.kind === "run" && run.setProgress(x.progress, { air: x.air }),
      // 길 방향(+x)을 보는 기본 돌기 위에 몸짓 돌기를 더한다(기울기 걸음은 길이 대신한다)
      applyPose: (p) => {
        run.heroPose.position.y = p.y;
        run.heroPose.rotation.set(p.rotX, Math.PI / 2 + p.rotY, p.rotZ);
      },
      animate: run.animate,
      lift: 0.3,
      dispose: run.dispose,
    };
  }
  if (t.kind === "plaza") {
    const plaza = r.createPlazaTemplate();
    let seen = t.n;
    return {
      object: plaza.object,
      update: (x) => {
        if (x.kind !== "plaza") return;
        plaza.setMembers(x.members);
        if (x.n > seen && x.last) plaza.packet(x.last);
        seen = x.n;
      },
      applyPose: (p) => {
        plaza.heroPose.position.y = p.y;
        plaza.heroPose.rotation.set(p.rotX, p.rotY, p.rotZ);
      },
      animate: plaza.animate,
      lift: 0.3,
      dispose: plaza.dispose,
    };
  }
  if (t.kind === "grid") {
    const grid = r.createGridTemplate({ n: t.n, treasure: t.treasure });
    return {
      object: grid.object,
      update: (x) => x.kind === "grid" && grid.setState(x),
      applyPose: (p) => {
        grid.heroPose.position.y = p.y;
        grid.heroPose.rotation.z = p.rotZ;
      },
      animate: grid.animate,
      lift: 0.26,
      dispose: grid.dispose,
    };
  }
  const hero = r.buildDecorItem({ figure: { char: "kid", at: [0, 0, 0], size: MB_HERO_SIZE } });
  if (!hero) throw new Error("hero");
  return {
    object: hero.object as StageObj["object"],
    update: () => {},
    applyPose: (p) => {
      hero.object.position.set(p.x, p.y, p.z);
      // 주인공은 카메라(+z) 쪽을 본다(캐릭터 앞 = +z)
      hero.object.rotation.set(p.rotX, p.rotY, p.rotZ);
    },
    animate: (tS) => (hero.animate(tS), false),
    lift: MB_HERO_SIZE + 0.08,
    dispose: hero.dispose,
  };
}
export const MB_FOLLOW = { back: -1.25, up: 0.45, side: 0.15, lookAhead: 0, stiffness: 3 };
/** 주인공 키(m) */
export const MB_HERO_SIZE = 0.3;

/** 템플릿에 맞는 무대 몸체 · 첫 자세(받침대 있음 · 없음) */
export const mbBodiesOf = (t: MbTemplate) =>
  t.kind === "pedestal"
    ? { bodies: MB_BODIES, tf: MB_TF }
    : { bodies: MB_FLAT_BODIES, tf: MB_FLAT_TF };
